import { isPerUnitUsageProductOnly,
  isTieredSubscriptionUsageProductOnly,
  isTiersUsageProductOnly,
  isTiersVolumeProduct } from '@weft/services';
import type { UsageProduct, UsageProductInput } from '@weft/types';
import { raiseUnreachable } from '@weft/utils';

export function getUsageProductsWithIndicesByType<Product extends UsageProduct | UsageProductInput>(usageProducts: Product[]) {
  const usageProductsWithIndices = usageProducts.map((product, productIndex) => {
    return {
      product,
      productIndex,
    };
  });

  return usageProductsWithIndices.reduce<Record<'Volume' | 'Tiers' | 'PerUnit' | 'TieredSubscription', typeof usageProductsWithIndices>>((usageProductsWithIndicesByType, productWithIndex) => {
    if (isTiersVolumeProduct(productWithIndex.product)) {
      return {
        ...usageProductsWithIndicesByType,
        Volume: [...usageProductsWithIndicesByType.Volume, productWithIndex],
      };
    }

    if (isTiersUsageProductOnly(productWithIndex.product)) {
      return {
        ...usageProductsWithIndicesByType,
        Tiers: [...usageProductsWithIndicesByType.Tiers, productWithIndex],
      };
    }

    if (isPerUnitUsageProductOnly(productWithIndex.product)) {
      return {
        ...usageProductsWithIndicesByType,
        PerUnit: [...usageProductsWithIndicesByType.PerUnit, productWithIndex],
      };
    }

    if (isTieredSubscriptionUsageProductOnly(productWithIndex.product)) {
      return {
        ...usageProductsWithIndicesByType,
        TieredSubscription: [...usageProductsWithIndicesByType.TieredSubscription, productWithIndex],
      };
    }

    return usageProductsWithIndicesByType;
  }, { Volume: [], Tiers: [], PerUnit: [], TieredSubscription: [] });
}

export const CHARGE_PROVIDER_SECTION_TITLES = Object.freeze({
  Volume: 'Volume Pricing',
  Tiers: 'Tier Pricing',
  PerUnit: 'Per Unit Pricing',
  TieredSubscription: 'Tiered Subscription Pricing',
});

export type ChargeProviderSectionTitles = (typeof CHARGE_PROVIDER_SECTION_TITLES)[keyof typeof CHARGE_PROVIDER_SECTION_TITLES];

export function getSectionTitle(title: 'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription') {
  switch (title) {
    case 'Tiers':
      return CHARGE_PROVIDER_SECTION_TITLES.Tiers;
    case 'PerUnit':
      return CHARGE_PROVIDER_SECTION_TITLES.PerUnit;
    case 'Volume':
      return CHARGE_PROVIDER_SECTION_TITLES.Volume;
    case 'TieredSubscription':
      return CHARGE_PROVIDER_SECTION_TITLES.TieredSubscription;
    default:
      return raiseUnreachable(title);
  }
}

export function getUsageProductsOrder(usageProducts: (UsageProduct | UsageProductInput)[]) {
  const usageProductsOrder = new Set<'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription'>();
  for (const product of usageProducts) {
    if (isTiersVolumeProduct(product)) {
      usageProductsOrder.add('Volume');
    } else if (isTiersUsageProductOnly(product)) {
      usageProductsOrder.add('Tiers');
    } else if (isPerUnitUsageProductOnly(product)) {
      usageProductsOrder.add('PerUnit');
    } else if (isTieredSubscriptionUsageProductOnly(product)) {
      usageProductsOrder.add('TieredSubscription');
    }
  }

  return usageProductsOrder;
}
